/* Absence Justifications — Educator/Director/Admin/Secretariat interface.
 * Path: /absences
 * Tabs:
 *   1. À traiter    → list of unjustified absences (existing flow + bulk)
 *   2. Journal      → chronological log with filters (iter48)
 * Fiche élève dialog now also includes:
 *   • Notes éducatives (historical, staff-only)  (iter48)
 *   • Convocation Parent (PDF)                    (iter48)
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { studentAttendanceApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ShieldCheck, PaperclipHorizontal, ClipboardText, XCircle, ArrowClockwise,
  Eye, UsersThree, BookOpenText, EnvelopeSimple, NotePencil, Trash,
} from '@phosphor-icons/react';

const AbsenceJustifications = () => {
  const [tab, setTab] = useState('pending');

  // ---- Pending tab state ----
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysBack, setDaysBack] = useState(30);
  const [classFilter, setClassFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [reason, setReason] = useState('');
  const [fileB64, setFileB64] = useState('');
  const [fileName, setFileName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // ---- Dossier + Notes + Convocation state ----
  const [dossierOpen, setDossierOpen] = useState(false);
  const [dossierLoading, setDossierLoading] = useState(false);
  const [dossierData, setDossierData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // ---- Convocation & motif templates ----
  const [convOpen, setConvOpen] = useState(false);
  const [convMotif, setConvMotif] = useState('');
  const [convRdv, setConvRdv] = useState('');
  const [convNote, setConvNote] = useState('');
  const [convBusy, setConvBusy] = useState(false);
  const [convNotify, setConvNotify] = useState(true);
  const [motifTemplates, setMotifTemplates] = useState([]);
  const [convocations, setConvocations] = useState([]);
  const [convocationsLoading, setConvocationsLoading] = useState(false);

  // ---- Journal tab state ----
  const [journalItems, setJournalItems] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalDays, setJournalDays] = useState(30);
  const [journalClass, setJournalClass] = useState('');
  const [journalUser, setJournalUser] = useState('');
  const [journalStatus, setJournalStatus] = useState('');
  const [journalIncludeAll, setJournalIncludeAll] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ classes: [], justifiers: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await studentAttendanceApi.pendingJustifications({
        daysBack: Number(daysBack) || 30,
        className: classFilter || undefined,
        limit: 200,
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur au chargement des absences');
    } finally {
      setLoading(false);
    }
  }, [daysBack, classFilter]);

  useEffect(() => { load(); }, [daysBack, load]);

  const classOptions = useMemo(() => {
    const set = new Set(items.map((i) => i.class_name).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => (
    classFilter ? items.filter((i) => i.class_name === classFilter) : items
  ), [items, classFilter]);

  // ============ Journal loader ============
  const loadJournalFilters = useCallback(async () => {
    try {
      const { data } = await studentAttendanceApi.journalFilters(90);
      setFilterOptions(data || { classes: [], justifiers: [] });
    } catch (e) {
      // silent
    }
  }, []);

  const loadJournal = useCallback(async () => {
    setJournalLoading(true);
    try {
      const { data } = await studentAttendanceApi.journal({
        daysBack: Number(journalDays) || 30,
        className: journalClass || undefined,
        byUserId: journalUser || undefined,
        statusFilter: journalStatus || undefined,
        includeAll: journalIncludeAll,
        limit: 300,
      });
      setJournalItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur au chargement du journal');
    } finally {
      setJournalLoading(false);
    }
  }, [journalDays, journalClass, journalUser, journalStatus, journalIncludeAll]);

  useEffect(() => {
    if (tab === 'journal') {
      loadJournalFilters();
      loadJournal();
    }
  }, [tab, loadJournalFilters, loadJournal]);

  // ============ Pending — actions ============
  const openJustify = (row) => {
    setCurrent(row); setReason(''); setFileB64(''); setFileName(''); setDialogOpen(true);
  };
  const onPickFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error('Fichier trop volumineux (>3 Mo)'); return; }
    const reader = new FileReader();
    reader.onload = () => { setFileB64(String(reader.result || '')); setFileName(f.name); };
    reader.readAsDataURL(f);
  };
  const submit = async () => {
    if (!current || !reason.trim()) { toast.error('Motif requis'); return; }
    setSubmitting(true);
    try {
      await studentAttendanceApi.justify(current._mongo_id || current.id, {
        reason: reason.trim(),
        document_base64: fileB64 || undefined,
        document_filename: fileName || undefined,
      });
      toast.success(`Absence de ${current.student_name} justifiée`);
      setDialogOpen(false);
      setItems((prev) => prev.filter((i) => (i._mongo_id || i.id) !== (current._mongo_id || current.id)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la justification');
    } finally { setSubmitting(false); }
  };
  const toggleSelected = (id) => setSelectedIds((prev) => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const toggleSelectAll = (visibleRows) => setSelectedIds((prev) => {
    const allIds = visibleRows.map((r) => r._mongo_id || r.id);
    const allSelected = allIds.every((id) => prev.has(id));
    return new Set(allSelected ? [] : allIds);
  });
  const submitBulk = async () => {
    if (selectedIds.size === 0 || !bulkReason.trim()) return;
    setBulkSubmitting(true);
    try {
      const { data } = await studentAttendanceApi.justifyBulk({
        attendance_ids: Array.from(selectedIds), reason: bulkReason.trim(),
      });
      toast.success(`${data.updated} absence${data.updated > 1 ? 's' : ''} justifiée${data.updated > 1 ? 's' : ''}`);
      setBulkOpen(false); setBulkReason(''); setSelectedIds(new Set());
      setItems((prev) => prev.filter((i) => !selectedIds.has(i._mongo_id || i.id)));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur bulk'); } finally { setBulkSubmitting(false); }
  };

  // ============ Dossier / notes / convocation ============
  const loadNotes = useCallback(async (studentId) => {
    setNotesLoading(true);
    try {
      const { data } = await studentAttendanceApi.educatorNotesList(studentId);
      setNotes(data.items || []);
    } catch (e) { setNotes([]); } finally { setNotesLoading(false); }
  }, []);

  const loadConvocations = useCallback(async (studentId) => {
    setConvocationsLoading(true);
    try {
      const { data } = await studentAttendanceApi.studentConvocations(studentId, 50);
      setConvocations(data.items || []);
    } catch (e) { setConvocations([]); } finally { setConvocationsLoading(false); }
  }, []);

  const openDossier = async (studentId) => {
    setDossierOpen(true); setDossierLoading(true); setDossierData(null); setNotes([]); setNewNote(''); setConvocations([]);
    try {
      const { data } = await studentAttendanceApi.studentDossier(studentId, 90);
      setDossierData(data);
      loadNotes(studentId);
      loadConvocations(studentId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur au chargement de la fiche');
    } finally { setDossierLoading(false); }
  };

  const downloadConvocationById = async (convId, studentName) => {
    try {
      const res = await studentAttendanceApi.convocationPdfById(convId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const safe = (studentName || 'eleve').replace(/\s+/g, '_');
      const a = document.createElement('a');
      a.href = url; a.download = `convocation-${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur téléchargement');
    }
  };

  const saveNote = async () => {
    if (!dossierData || !newNote.trim()) return;
    setSavingNote(true);
    try {
      const sid = dossierData.student.id;
      await studentAttendanceApi.educatorNoteAdd(sid, newNote.trim());
      setNewNote('');
      await loadNotes(sid);
      toast.success('Note ajoutée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur ajout note');
    } finally { setSavingNote(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      await studentAttendanceApi.educatorNoteDelete(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success('Note supprimée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openConvocation = () => {
    setConvMotif(''); setConvNote(''); setConvBusy(false); setConvNotify(true);
    // Default RDV: J+3 at 10:00 local
    const d = new Date(); d.setDate(d.getDate() + 3); d.setHours(10, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    setConvRdv(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    setConvOpen(true);
    // Load templates lazily once
    if (motifTemplates.length === 0) {
      studentAttendanceApi.convocationMotifTemplates()
        .then(({ data }) => setMotifTemplates(data.templates || []))
        .catch(() => setMotifTemplates([]));
    }
  };

  const applyMotifTemplate = (tplId) => {
    if (!tplId) return;
    const t = motifTemplates.find((x) => x.id === tplId);
    if (t) setConvMotif(t.text);
  };

  const generateConvocation = async () => {
    if (!dossierData || !convMotif.trim() || !convRdv.trim()) {
      toast.error('Motif et date de RDV requis'); return;
    }
    setConvBusy(true);
    try {
      const sid = dossierData.student.id;
      const res = await studentAttendanceApi.convocationPdf(sid, {
        motif: convMotif.trim(), rdv_datetime: convRdv,
        note: convNote.trim() || undefined,
        notify_parents: convNotify,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const safe = `${dossierData.student.last_name || ''}-${dossierData.student.first_name || ''}`.replace(/\s+/g, '_') || 'eleve';
      const a = document.createElement('a');
      a.href = url; a.download = `convocation-${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      const push = res.headers?.['x-convocation-notified-push'] || res.headers?.['X-Convocation-Notified-Push'] || '0';
      const mail = res.headers?.['x-convocation-notified-email'] || res.headers?.['X-Convocation-Notified-Email'] || '0';
      if (convNotify) {
        toast.success(`Convocation générée · ${push} push · ${mail} email envoyé(s)`);
      } else {
        toast.success('Convocation générée');
      }
      setConvOpen(false);
      // Refresh historique so the new convocation appears immediately
      if (dossierData?.student?.id) {
        loadConvocations(dossierData.student.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération convocation');
    } finally { setConvBusy(false); }
  };

  const downloadJustificationPdf = async (attendanceId, studentName) => {
    try {
      const res = await studentAttendanceApi.justificationPdf(attendanceId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const safe = (studentName || 'eleve').replace(/\s+/g, '_');
      const a = document.createElement('a');
      a.href = url; a.download = `justificatif-${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur téléchargement');
    }
  };

  const statusPill = (s) => (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
      s === 'present' ? 'bg-emerald-100 text-emerald-700' :
      s === 'absent' ? 'bg-red-100 text-red-700' :
      s === 'retard' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
    }`}>{s?.toUpperCase()}</span>
  );

  return (
    <div className="p-6 space-y-4" data-testid="absence-justifications-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-outfit text-2xl font-bold flex items-center gap-2">
            <ClipboardText size={26} className="text-red-600" weight="duotone" />
            Justification des absences
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Absences non justifiées à traiter et journal des relevés — éducateurs, direction ou secrétariat.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="absences-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending">
            <ClipboardText size={16} className="mr-1.5" /> À traiter
          </TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal">
            <BookOpenText size={16} className="mr-1.5" /> Journal
          </TabsTrigger>
        </TabsList>

        {/* ============== Pending tab ============== */}
        <TabsContent value="pending" className="space-y-4">
          <div className="flex justify-end">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="absences-refresh-btn">
              <ArrowClockwise size={16} className="mr-2" /> Rafraîchir
            </Button>
          </div>
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-medium text-[#475569]">Période</Label>
                <select value={daysBack} onChange={(e) => setDaysBack(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="absences-period-select">
                  <option value="7">7 derniers jours</option>
                  <option value="14">14 derniers jours</option>
                  <option value="30">30 derniers jours</option>
                  <option value="90">90 derniers jours</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#475569]">Classe</Label>
                <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="absences-class-filter">
                  <option value="">Toutes les classes</option>
                  {classOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <div className="px-3 py-2 rounded-md bg-red-50 text-red-700 text-sm font-medium w-full text-center" data-testid="absences-count">
                  {filtered.length} absence{filtered.length > 1 ? 's' : ''} à justifier
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-[#94A3B8]">Chargement…</div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center" data-testid="absences-empty">
                  <ShieldCheck size={40} className="mx-auto text-emerald-500 mb-2" weight="duotone" />
                  <p className="font-outfit font-semibold text-[#0F172A]">Aucune absence à justifier</p>
                  <p className="text-sm text-[#64748B]">Tout est à jour sur la période sélectionnée.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input type="checkbox"
                          checked={filtered.length > 0 && filtered.every(r => selectedIds.has(r._mongo_id || r.id))}
                          onChange={() => toggleSelectAll(filtered)}
                          className="w-4 h-4 cursor-pointer" data-testid="absences-selectall-checkbox" />
                      </TableHead>
                      <TableHead>Élève</TableHead>
                      <TableHead>Classe</TableHead>
                      <TableHead>Cycle</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Matière</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((row) => {
                      const rowId = row._mongo_id || row.id;
                      return (
                        <TableRow key={rowId} data-testid={`absence-row-${rowId}`}
                          className={selectedIds.has(rowId) ? 'bg-emerald-50/50' : undefined}>
                          <TableCell>
                            <input type="checkbox" checked={selectedIds.has(rowId)}
                              onChange={() => toggleSelected(rowId)}
                              className="w-4 h-4 cursor-pointer" data-testid={`absence-select-${rowId}`} />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {row.photo_base64 ? (
                                <img src={row.photo_base64} alt="" className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-[#F1F5F9] flex items-center justify-center text-xs font-semibold text-[#64748B]">
                                  {(row.student_name || '').split(' ').map(p => p[0]).join('').slice(0, 2)}
                                </div>
                              )}
                              <span className="font-medium text-sm">{row.student_name}</span>
                            </div>
                          </TableCell>
                          <TableCell><span className="font-mono text-xs">{row.class_name}</span></TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              row.cycle === 'secondaire' ? 'bg-purple-100 text-purple-700' :
                              row.cycle === 'primaire' ? 'bg-blue-100 text-blue-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>{row.cycle || '—'}</span>
                          </TableCell>
                          <TableCell className="text-sm">{row.date}</TableCell>
                          <TableCell className="text-xs text-[#64748B]">
                            {row.subject || (row.slot_key === 'daily' ? 'Journée entière' : '—')}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" onClick={() => openDossier(row.student_id)}
                                className="border-slate-300 text-slate-700 hover:bg-slate-100"
                                data-testid={`absence-view-file-btn-${rowId}`}>
                                <Eye size={14} className="mr-1" /> Fiche
                              </Button>
                              <Button size="sm" onClick={() => openJustify(row)}
                                className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid={`absence-justify-btn-${rowId}`}>
                                <ShieldCheck size={14} className="mr-1" /> Justifier
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== Journal tab ============== */}
        <TabsContent value="journal" className="space-y-4">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs font-medium text-[#475569]">Période</Label>
                <select value={journalDays} onChange={(e) => setJournalDays(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="journal-period-select">
                  <option value="7">7 j</option>
                  <option value="14">14 j</option>
                  <option value="30">30 j</option>
                  <option value="90">90 j</option>
                  <option value="180">180 j</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#475569]">Classe</Label>
                <select value={journalClass} onChange={(e) => setJournalClass(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="journal-class-filter">
                  <option value="">Toutes</option>
                  {filterOptions.classes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#475569]">Éducateur / Signataire</Label>
                <select value={journalUser} onChange={(e) => setJournalUser(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="journal-user-filter">
                  <option value="">Tous</option>
                  {filterOptions.justifiers.map((j) => (
                    <option key={j.user_id} value={j.user_id}>{j.name || j.user_id.slice(0, 8)} ({j.role})</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#475569]">Statut</Label>
                <select value={journalStatus} onChange={(e) => setJournalStatus(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="journal-status-filter">
                  <option value="">Tous</option>
                  <option value="present">Présents</option>
                  <option value="absent">Absents</option>
                  <option value="retard">Retards</option>
                  <option value="excuse">Excusés</option>
                </select>
              </div>
              <div className="flex flex-col justify-end gap-1">
                <label className="text-xs text-[#475569] flex items-center gap-2">
                  <input type="checkbox" checked={journalIncludeAll}
                    onChange={(e) => setJournalIncludeAll(e.target.checked)}
                    data-testid="journal-includeall-checkbox" />
                  Tous événements
                </label>
                <Button size="sm" onClick={loadJournal} disabled={journalLoading}
                  className="bg-slate-800 hover:bg-slate-900" data-testid="journal-apply-btn">
                  <ArrowClockwise size={14} className="mr-1" /> Appliquer
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              {journalLoading ? (
                <div className="p-8 text-center text-[#94A3B8]">Chargement…</div>
              ) : journalItems.length === 0 ? (
                <div className="p-10 text-center" data-testid="journal-empty">
                  <BookOpenText size={40} className="mx-auto text-slate-400 mb-2" weight="duotone" />
                  <p className="font-outfit font-semibold text-[#0F172A]">Aucun événement</p>
                  <p className="text-sm text-[#64748B]">Ajustez les filtres pour élargir la recherche.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Élève</TableHead>
                      <TableHead>Classe</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Justification / Motif</TableHead>
                      <TableHead>Signé par</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {journalItems.map((r) => (
                      <TableRow key={r.attendance_id} data-testid={`journal-row-${r.attendance_id}`}>
                        <TableCell className="font-mono text-xs">
                          {r.date}
                          {r.at && <div className="text-[10px] text-[#94A3B8]">justifié {new Date(r.at).toLocaleDateString('fr-FR')}</div>}
                        </TableCell>
                        <TableCell className="text-sm font-medium">{r.student_name}</TableCell>
                        <TableCell><span className="font-mono text-xs">{r.class_name}</span></TableCell>
                        <TableCell>{statusPill(r.status)}</TableCell>
                        <TableCell className="text-xs text-[#475569]">
                          {r.reason ? (
                            <span className="italic">« {r.reason.slice(0, 80)}{r.reason.length > 80 ? '…' : ''} »</span>
                          ) : (
                            <span className="text-[#94A3B8]">—</span>
                          )}
                          {r.has_document && (
                            <PaperclipHorizontal size={12} className="inline ml-1 text-emerald-600" />
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.by_user_name ? (
                            <>
                              <div className="font-medium">{r.by_user_name}</div>
                              <div className="text-[10px] text-[#94A3B8]">{r.by_role}</div>
                            </>
                          ) : (
                            <span className="text-[#94A3B8]">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {r.at && (
                              <Button size="sm" variant="outline" onClick={() => downloadJustificationPdf(r.attendance_id, r.student_name)}
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                data-testid={`journal-download-pdf-${r.attendance_id}`}>
                                <ClipboardText size={12} className="mr-1" /> PDF
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => openDossier(r.student_id)}
                              className="border-slate-300 text-slate-700 hover:bg-slate-100"
                              data-testid={`journal-view-file-${r.attendance_id}`}>
                              <Eye size={12} className="mr-1" /> Fiche
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-emerald-600 text-white px-4 py-2.5 rounded-full shadow-lg flex items-center gap-3"
          data-testid="absences-bulk-bar">
          <span className="text-sm font-semibold">{selectedIds.size} sélectionnée{selectedIds.size > 1 ? 's' : ''}</span>
          <Button size="sm" onClick={() => setBulkOpen(true)}
            className="bg-white text-emerald-700 hover:bg-emerald-50"
            data-testid="absences-bulk-open-btn">
            <UsersThree size={14} className="mr-1" /> Justifier en masse
          </Button>
          <button onClick={() => setSelectedIds(new Set())}
            className="text-xs text-emerald-100 hover:text-white"
            data-testid="absences-bulk-clear">Effacer</button>
        </div>
      )}

      {/* Bulk dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-justify-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              Justifier {selectedIds.size} absence{selectedIds.size > 1 ? 's' : ''} en masse
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Motif *</Label>
            <Textarea value={bulkReason} onChange={(e) => setBulkReason(e.target.value)}
              placeholder="Ex : Maladie prolongée du…" rows={4} maxLength={1000}
              data-testid="bulk-justify-reason-input" />
            <p className="text-xs text-[#94A3B8]">
              Le même motif sera appliqué aux {selectedIds.size} absences sélectionnées.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkSubmitting}>Annuler</Button>
            <Button onClick={submitBulk} disabled={bulkSubmitting || !bulkReason.trim()}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-justify-submit-btn">
              <ShieldCheck size={16} className="mr-2" />
              {bulkSubmitting ? 'Envoi…' : 'Justifier tout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fiche élève dialog */}
      <Dialog open={dossierOpen} onOpenChange={setDossierOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dossier-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Eye size={20} className="text-slate-600" weight="duotone" /> Fiche élève
            </DialogTitle>
          </DialogHeader>
          {dossierLoading ? (
            <p className="text-sm text-[#94A3B8] text-center py-6">Chargement…</p>
          ) : dossierData ? (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center gap-3 pb-3 border-b border-[#E2E8F0]">
                {dossierData.student.photo_base64 ? (
                  <img src={dossierData.student.photo_base64} alt="" className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-lg font-bold text-slate-500">
                    {(dossierData.student.first_name?.[0] || '') + (dossierData.student.last_name?.[0] || '')}
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-outfit text-lg font-bold">
                    {dossierData.student.first_name} {dossierData.student.last_name}
                  </h3>
                  <p className="text-xs text-[#64748B]">
                    {dossierData.student.class_name} · Cycle {dossierData.student.cycle}
                  </p>
                </div>
                <Button onClick={openConvocation}
                  className="bg-[#0052FF] hover:bg-[#0044D4] text-white"
                  data-testid="convocation-open-btn">
                  <EnvelopeSimple size={16} className="mr-1.5" /> Convocation Parent
                </Button>
              </div>

              {/* Counts */}
              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <div className="p-2 bg-emerald-50 rounded"><div className="font-bold text-emerald-700 text-lg">{dossierData.counts.present}</div>Présents</div>
                <div className="p-2 bg-red-50 rounded"><div className="font-bold text-red-700 text-lg">{dossierData.counts.absent}</div>Absents</div>
                <div className="p-2 bg-amber-50 rounded"><div className="font-bold text-amber-700 text-lg">{dossierData.counts.retard}</div>Retards</div>
                <div className="p-2 bg-slate-100 rounded"><div className="font-bold text-slate-700 text-lg">{dossierData.counts.excuse}</div>Excusés</div>
              </div>
              <div className="text-xs text-[#64748B] text-center">
                {dossierData.counts.absent_justified} absence(s) justifiée(s) sur {dossierData.counts.absent} · depuis le {dossierData.since}
              </div>

              {/* Parents */}
              {dossierData.parents.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase font-bold text-[#64748B] mb-1">Parents liés</h4>
                  <ul className="space-y-1">
                    {dossierData.parents.map((p, i) => (
                      <li key={i} className="text-sm p-2 bg-slate-50 rounded flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-medium">{p.name}</span>
                        {p.email && <a href={`mailto:${p.email}`} className="text-xs text-[#0052FF] hover:underline">{p.email}</a>}
                        {p.phone && <a href={`tel:${p.phone}`} className="text-xs text-emerald-700 hover:underline">{p.phone}</a>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notes éducatives */}
              <div>
                <h4 className="text-xs uppercase font-bold text-[#64748B] mb-1 flex items-center gap-1">
                  <NotePencil size={14} weight="duotone" /> Notes éducatives (staff)
                </h4>
                <div className="flex gap-2 mb-2">
                  <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Nouvelle note (comportement, entretien, suivi)…"
                    rows={2} maxLength={2000}
                    className="flex-1 text-sm" data-testid="note-input" />
                  <Button onClick={saveNote} disabled={savingNote || !newNote.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 self-start"
                    data-testid="note-save-btn">
                    {savingNote ? '…' : 'Ajouter'}
                  </Button>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1.5" data-testid="notes-list">
                  {notesLoading ? (
                    <p className="text-xs text-[#94A3B8]">Chargement…</p>
                  ) : notes.length === 0 ? (
                    <p className="text-xs text-[#94A3B8] italic">Aucune note pour cet élève.</p>
                  ) : notes.map((n) => (
                    <div key={n.id} className="p-2 bg-slate-50 rounded text-xs" data-testid={`note-item-${n.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-700">
                          {n.by_user_name || '—'} <span className="text-[10px] font-normal text-[#94A3B8]">· {n.by_role}</span>
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#94A3B8]">
                            {n.created_at ? new Date(n.created_at).toLocaleString('fr-FR') : ''}
                          </span>
                          <button onClick={() => deleteNote(n.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`note-delete-${n.id}`}>
                            <Trash size={12} />
                          </button>
                        </div>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-slate-800">{n.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Historique convocations — iter50 */}
              <div>
                <h4 className="text-xs uppercase font-bold text-[#64748B] mb-1 flex items-center gap-1">
                  <EnvelopeSimple size={14} weight="duotone" /> Historique convocations
                </h4>
                {convocationsLoading ? (
                  <p className="text-xs text-[#94A3B8]">Chargement…</p>
                ) : convocations.length === 0 ? (
                  <p className="text-xs text-[#94A3B8] italic">Aucune convocation émise pour cet élève.</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto border border-[#E2E8F0] rounded" data-testid="convocations-list">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F8FAFC] sticky top-0">
                        <tr>
                          <th className="p-1.5 text-left">RDV</th>
                          <th className="p-1.5 text-left">Motif</th>
                          <th className="p-1.5 text-left">Émise par</th>
                          <th className="p-1.5 text-center">Réponse</th>
                          <th className="p-1.5 text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {convocations.map((c) => (
                          <tr key={c.id} className="border-t border-[#E2E8F0]" data-testid={`convocation-row-${c.id}`}>
                            <td className="p-1.5 font-mono whitespace-nowrap">
                              {c.rdv_datetime?.replace('T', ' ').slice(0, 16) || '—'}
                            </td>
                            <td className="p-1.5 italic text-[#475569] max-w-[240px] truncate" title={c.motif}>
                              {c.motif?.slice(0, 60)}{(c.motif || '').length > 60 && '…'}
                            </td>
                            <td className="p-1.5">
                              <div className="font-medium">{c.by_user_name || '—'}</div>
                              <div className="text-[10px] text-[#94A3B8]">
                                {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : ''}
                                {' · '}
                                {c.notified_parents_push || 0}p · {c.notified_parents_email || 0}m
                              </div>
                            </td>
                            <td className="p-1.5 text-center">
                              {c.parent_response === 'accept' ? (
                                <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold text-[10px]">
                                  Présent
                                </span>
                              ) : c.parent_response === 'decline' ? (
                                <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold text-[10px]">
                                  Indispo.
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium text-[10px]">
                                  En attente
                                </span>
                              )}
                            </td>
                            <td className="p-1.5 text-right">
                              <button
                                onClick={() => downloadConvocationById(c.id, dossierData.student.first_name + ' ' + dossierData.student.last_name)}
                                className="text-[10px] text-[#0052FF] hover:underline font-semibold"
                                data-testid={`convocation-download-${c.id}`}
                              >
                                PDF
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Historique */}
              <div>
                <h4 className="text-xs uppercase font-bold text-[#64748B] mb-1">Historique (90 j) — {dossierData.rows.length} relevés</h4>
                <div className="max-h-56 overflow-y-auto border border-[#E2E8F0] rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F8FAFC] sticky top-0">
                      <tr><th className="p-1.5 text-left">Date</th><th className="p-1.5 text-left">Statut</th><th className="p-1.5 text-left">Justif</th><th className="p-1.5 text-right"></th></tr>
                    </thead>
                    <tbody>
                      {dossierData.rows.slice(0, 60).map((r) => (
                        <tr key={r._mongo_id} className="border-t border-[#E2E8F0]">
                          <td className="p-1.5 font-mono">{r.date}</td>
                          <td className="p-1.5">{statusPill(r.status)}</td>
                          <td className="p-1.5 text-[#64748B] italic">
                            {r.justification
                              ? `${(r.justification.reason || '').slice(0, 40)}${(r.justification.reason || '').length > 40 ? '…' : ''} — ${r.justification.by_user_name || '—'}`
                              : r.status === 'absent' ? 'à justifier' : ''}
                          </td>
                          <td className="p-1.5 text-right">
                            {r.justification && (
                              <button
                                onClick={() => downloadJustificationPdf(r._mongo_id, `${dossierData.student.first_name} ${dossierData.student.last_name}`)}
                                className="text-[10px] text-emerald-700 hover:text-emerald-900 hover:underline"
                                data-testid={`fiche-justif-pdf-${r._mongo_id}`}
                              >
                                PDF
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-red-600">Impossible de charger la fiche.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDossierOpen(false)} data-testid="dossier-close-btn">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convocation dialog */}
      <Dialog open={convOpen} onOpenChange={setConvOpen}>
        <DialogContent className="max-w-lg" data-testid="convocation-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <EnvelopeSimple size={20} weight="duotone" className="text-[#0052FF]" />
              Générer la convocation
              {dossierData && <span className="block text-sm font-normal text-[#64748B]">
                {dossierData.student.first_name} {dossierData.student.last_name}
              </span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Modèle de motif</Label>
              <select
                onChange={(e) => applyMotifTemplate(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                data-testid="convocation-template-select"
                defaultValue=""
              >
                <option value="">— Choisir un modèle (optionnel) —</option>
                {motifTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-[#94A3B8] mt-1">
                Sélectionnez un modèle pour pré-remplir le motif, puis modifiez-le librement.
              </p>
            </div>
            <div>
              <Label>Motif du rendez-vous *</Label>
              <Textarea value={convMotif} onChange={(e) => setConvMotif(e.target.value)}
                placeholder="Ex : Absences répétées et non-justifiées — entretien de suivi"
                rows={4} maxLength={500}
                className="mt-1" data-testid="convocation-motif-input" />
            </div>
            <div>
              <Label>Date et heure du rendez-vous *</Label>
              <Input type="datetime-local" value={convRdv}
                onChange={(e) => setConvRdv(e.target.value)}
                className="mt-1" data-testid="convocation-rdv-input" />
            </div>
            <div>
              <Label>Complément d&apos;information (optionnel)</Label>
              <Textarea value={convNote} onChange={(e) => setConvNote(e.target.value)}
                placeholder="Ex : Merci d'apporter le carnet de correspondance."
                rows={2} maxLength={500}
                className="mt-1" data-testid="convocation-note-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#475569]">
              <input type="checkbox" checked={convNotify}
                onChange={(e) => setConvNotify(e.target.checked)}
                data-testid="convocation-notify-checkbox" />
              Notifier automatiquement les parents (push + e-mail avec PDF joint)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvOpen(false)} disabled={convBusy}>Annuler</Button>
            <Button onClick={generateConvocation}
              disabled={convBusy || !convMotif.trim() || !convRdv.trim()}
              className="bg-[#0052FF] hover:bg-[#0044D4]"
              data-testid="convocation-submit-btn">
              <EnvelopeSimple size={16} className="mr-1.5" />
              {convBusy ? 'Génération…' : 'Générer le PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Justify dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setCurrent(null); }}>
        <DialogContent className="max-w-lg" data-testid="justify-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              Justifier l&apos;absence
              {current && <span className="block text-sm font-normal text-[#64748B] mt-1">
                {current.student_name} · {current.class_name} · {current.date}
                {current.subject && ` · ${current.subject}`}
              </span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Motif *</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Ex : Maladie, rendez-vous médical, motif familial…"
                rows={4} maxLength={1000} data-testid="justify-reason-input" className="mt-1" />
              <div className="text-[10px] text-[#94A3B8] text-right">{reason.length}/1000</div>
            </div>
            <div>
              <Label>Justificatif scanné (image ou PDF, ≤3 Mo — optionnel)</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={onPickFile} className="mt-1"
                data-testid="justify-file-input" />
              {fileName && (
                <div className="mt-2 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                  <PaperclipHorizontal size={14} />
                  <span className="truncate">{fileName}</span>
                  <button onClick={() => { setFileB64(''); setFileName(''); }}
                    className="ml-auto text-red-600 hover:text-red-700" data-testid="justify-file-clear-btn">
                    <XCircle size={14} />
                  </button>
                </div>
              )}
              {fileB64 && fileB64.startsWith('data:image') && (
                <img src={fileB64} alt="preview" className="mt-2 max-h-48 rounded border border-[#E2E8F0]" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting} data-testid="justify-cancel-btn">Annuler</Button>
            <Button onClick={submit} disabled={submitting || !reason.trim()}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="justify-submit-btn">
              <ShieldCheck size={16} className="mr-2" />
              {submitting ? 'Envoi…' : 'Justifier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AbsenceJustifications;
